import {
	BarChart3,
	Bell,
	Building2,
	FileCheck2,
	Landmark,
	Search,
	ShieldCheck,
	TrendingUp,
} from "lucide-react";
import {
	type SegmentLandingConfig,
	SegmentLandingPage,
} from "~/components/marketing/segment-landing-page";
import { generateMetaTags, SITE_URL } from "~/lib/seo";

export function meta() {
	return generateMetaTags({
		title: "FirmIndex pro finanční, credit control a nákupní týmy",
		description:
			"Prověřte dostupnou finanční kondici odběratele nebo dodavatele a sledujte důležité změny. FirmIndex můžete vyzkoušet zdarma.",
		canonical: `${SITE_URL}/pro-financni-tymy`,
		noindex: true,
	});
}

const config = {
	eyebrow: "Pro finance, credit control a nákup",
	titleLead: "Prověřte firmu dřív, než jí dáte",
	titleHighlight: "obchodní úvěr nebo důležitou zakázku.",
	description:
		"Srovnejte dostupný finanční vývoj, insolvenci, stav DPH a důležité změny odběratele či dodavatele — potom si firmu ponechte v monitoringu.",
	heroNote:
		"FirmIndex poskytuje informační podklady. Nejde o úvěrový rating, doporučení kreditního limitu ani garanci budoucí platby.",
	proofPoints: ["14 dní Premium", "Bez platební karty", "Monitoring obchodních partnerů"],
	preview: {
		overline: "Prověření partnera",
		title: "Podklady před rozhodnutím",
		subtitle: "Rychlý kontext k firmě před splatností, zálohou nebo klíčovou objednávkou.",
		badge: "Ilustrační profil",
		items: [
			{
				icon: TrendingUp,
				label: "Finanční vývoj",
				value: "Tržby a výsledek v čase",
			},
			{
				icon: ShieldCheck,
				label: "Veřejné registry",
				value: "Insolvence a stav DPH v přehledu",
			},
			{
				icon: Bell,
				label: "Průběžná kontrola",
				value: "Nové změny ve sledování",
			},
		],
	},
	valueOverline: "Před obchodem i po něm",
	valueHeading: "Méně nejistoty u firem, kterým svěřujete peníze nebo dodávku",
	valueDescription:
		"Jeden údaj sám o sobě nerozhodne. Společný kontext financí, registrů a změn ale pomůže položit správné otázky dřív.",
	valueCards: [
		{
			icon: Search,
			title: "Prověření před splatností",
			description:
				"Před vystavením faktury se splatností si projděte dostupné finance, insolvenci, DPH a osoby ve firmě.",
		},
		{
			icon: Building2,
			title: "Kontrola dodavatele",
			description:
				"Stejný přehled využijete před zálohou, výběrem subdodavatele nebo uzavřením dlouhodobé spolupráce.",
		},
		{
			icon: Bell,
			title: "Monitoring během vztahu",
			description:
				"Sledujte insolvenci, změny ve vedení a nové listiny u firem, na kterých závisí vaše pohledávky nebo dodávky.",
		},
	],
	workflowHeading: "Prověřit, rozhodnout podle vlastních pravidel, dál sledovat",
	workflowDescription:
		"FirmIndex dodá veřejná data a souvislosti. Kreditní nebo nákupní rozhodnutí zůstává na vašich interních pravidlech a znalosti obchodního vztahu.",
	workflowSteps: [
		{
			icon: Search,
			title: "Vyhledejte odběratele nebo dodavatele",
			description:
				"Stačí název nebo IČO a otevřete dostupné finanční, registrační a dokumentové podklady.",
		},
		{
			icon: BarChart3,
			title: "Posuďte vývoj v souvislostech",
			description:
				"Porovnejte více období a zkontrolujte důležité změny, nikoli pouze jednu izolovanou hodnotu.",
		},
		{
			icon: Bell,
			title: "Přidejte klíčovou firmu do monitoringu",
			description:
				"Když se objeví sledovaná změna, uvidíte ji v přehledu bez nového ručního prověřování.",
		},
	],
	depthOverline: "Více než jednorázový výpis",
	depthHeading: "Čísla, registry a firemní události v jednom kontextu",
	depthDescription:
		"Finanční vývoj je důležitý, ale nové riziko může být vidět také v insolvenci, změně statutára nebo právě zveřejněné listině.",
	depthCards: [
		{
			icon: BarChart3,
			title: "Vývoj dostupných financí",
			description:
				"Tržby, výsledek hospodaření, majetek a další zveřejněné položky uvidíte napříč účetními obdobími.",
		},
		{
			icon: Landmark,
			title: "Stav ve veřejných registrech",
			description:
				"Insolvence, registrace k DPH, osoby a další dostupné údaje jsou propojené podle jedné firmy.",
		},
		{
			icon: FileCheck2,
			title: "Události z nových listin",
			description:
				"Zveřejněné projekty přeměn nebo notářské zápisy mohou doplnit kontext k tomu, co se ve firmě mění.",
		},
	],
	legalHeading: "Podklad pro vaše rozhodnutí, ne rozhodnutí samotné",
	legalDescription:
		"Dostupnost finančních údajů závisí na tom, co daná firma zveřejnila. Automaticky vytěžené údaje mohou obsahovat chyby a je třeba je posuzovat spolu s dalšími informacemi.",
	legalNote:
		"FirmIndex není ratingová agentura, neurčuje kreditní limit a nepředpovídá, zda firma zaplatí. Před významným obchodním rozhodnutím ověřte podstatné údaje v původním zdroji a použijte vlastní pravidla řízení rizik.",
	trialHeading: "Prověřte si vlastního odběratele nebo dodavatele zdarma",
	trialDescription:
		"Použijte FirmIndex na firmě, kterou znáte, a porovnejte přehled s vlastní zkušeností. Premium vyzkoušíte na 14 dní bez platební karty.",
} satisfies SegmentLandingConfig;

export default function ProFinancniTymy() {
	return <SegmentLandingPage config={config} />;
}
